import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator, User, Phone, MapPin, Building2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { provinces } from '../mockData';
import { getLeadTrackingFields } from '../utils/leadTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const clientTypes = ['Privato', 'Azienda'];
const buildingTypes = ['Casa singola', 'Villa', 'Villetta a schiera', 'Condominio', 'Capannone industriale', 'Edificio commerciale', 'Altro'];
const ownerTypes = ['Proprietario', 'Amministratore', 'Affittuario'];
const structureTypes = [
  'Tetto in tegole/coppi',
  'Tetto in lamiera',
  'Tetto in eternit/amianto',
  'Tetto piano/terrazza',
  'Copertura industriale',
  'Altro',
];
const surfaceRanges = ['Meno di 50 m²', '50-100 m²', '100-200 m²', '200-500 m²', 'Oltre 500 m²'];

const CalcolatoreRisparmio = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    clientType: '',
    buildingType: '',
    ownerType: '',
    structureType: '',
    surface: '',
    province: '',
    name: '',
    phone: '',
    email: '',
    website: '',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canProceed1 = form.clientType && form.buildingType && form.ownerType;
  const canProceed2 = form.structureType && form.surface && form.province;
  const canSubmit = form.name && form.phone && form.email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);

    const desc = `Calcolatore Risparmio | Cliente: ${form.clientType} | Edificio: ${form.buildingType} | Ruolo: ${form.ownerType} | Struttura: ${form.structureType} | Superficie: ${form.surface}`;

    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email || '',
        province: form.province,
        houseType: form.buildingType,
        ownershipStatus: form.ownerType,
        problemDescription: desc,
        website: form.website,
        tipologiaTetto: form.structureType,
        metri: form.surface,
        tipoIntervento: 'Calcolatore Risparmio',
        ...getLeadTrackingFields(),
      });

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17960264240/HNy_CMqrr_obELDEj_RC',
          value: 1.0,
          currency: 'EUR',
        });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
      }

      navigate('/grazie');
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClass = "bg-white border-slate-200 text-slate-900";

  return (
    <section id="calcolatore" className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,87,34,0.04),transparent_50%)]" />

      <div className="max-w-4xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">Calcola il tuo risparmio</span>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-3">
            Quanto puoi risparmiare senza ponteggi?
          </h2>
          <p className="mt-4 text-slate-500 max-w-xl mx-auto">
            Rispondi a poche domande e ricevi un preventivo personalizzato gratuito.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-lg shadow-slate-200/50"
        >
          {/* Progress */}
          <div className="flex border-b border-slate-200">
            {['Chi sei', 'La tua struttura', 'I tuoi dati'].map((label, i) => (
              <div
                key={label}
                className={`flex-1 py-4 text-center text-sm font-medium transition-colors ${
                  step === i + 1
                    ? 'text-[#FF5722] bg-[#FF5722]/5 border-b-2 border-[#FF5722]'
                    : step > i + 1
                    ? 'text-[#FF5722]/60'
                    : 'text-slate-400'
                }`}
              >
                <span className="hidden sm:inline">{`${i + 1}. `}</span>{label}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-8" data-testid="calculator-form">
            {/* Step 1: Chi sei */}
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <label className="text-slate-700 text-sm font-medium mb-2 block">Tipologia cliente *</label>
                  <div className="grid grid-cols-2 gap-3">
                    {clientTypes.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => update('clientType', t)}
                        data-testid={`client-type-${t.toLowerCase()}`}
                        className={`py-3 rounded-xl text-sm font-medium transition-all border ${
                          form.clientType === t
                            ? 'bg-[#FF5722]/10 border-[#FF5722] text-[#FF5722]'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-slate-700 text-sm font-medium mb-2 block">Tipologia abitazione *</label>
                  <Select value={form.buildingType} onValueChange={(v) => update('buildingType', v)}>
                    <SelectTrigger data-testid="calc-building-type" className={selectClass}>
                      <SelectValue placeholder="Seleziona tipologia" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildingTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-slate-700 text-sm font-medium mb-2 block">Il tuo ruolo *</label>
                  <div className="grid grid-cols-3 gap-3">
                    {ownerTypes.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => update('ownerType', t)}
                        data-testid={`owner-type-${t.toLowerCase()}`}
                        className={`py-3 rounded-xl text-sm font-medium transition-all border ${
                          form.ownerType === t
                            ? 'bg-[#FF5722]/10 border-[#FF5722] text-[#FF5722]'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!canProceed1}
                  onClick={() => setStep(2)}
                  className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 disabled:hover:bg-[#FF5722] text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  Continua <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Step 2: La tua struttura */}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <label className="text-slate-700 text-sm font-medium mb-2 block">Tipologia di copertura *</label>
                  <Select value={form.structureType} onValueChange={(v) => update('structureType', v)}>
                    <SelectTrigger data-testid="calc-structure-type" className={selectClass}>
                      <SelectValue placeholder="Seleziona il tipo di tetto" />
                    </SelectTrigger>
                    <SelectContent>
                      {structureTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-slate-700 text-sm font-medium mb-2 block">Superficie approssimativa *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {surfaceRanges.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => update('surface', s)}
                        className={`py-3 px-2 rounded-xl text-sm font-medium transition-all border ${
                          form.surface === s
                            ? 'bg-[#FF5722]/10 border-[#FF5722] text-[#FF5722]'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-slate-700 text-sm font-medium mb-2 block">Provincia *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                    <Select value={form.province} onValueChange={(v) => update('province', v)}>
                      <SelectTrigger data-testid="calc-province" className={`${selectClass} pl-10`}>
                        <SelectValue placeholder="Seleziona provincia" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {provinces.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-3.5 rounded-xl text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
                  >
                    Indietro
                  </button>
                  <button
                    type="button"
                    disabled={!canProceed2}
                    onClick={() => setStep(3)}
                    className="flex-1 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    Continua <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: I tuoi dati */}
            {step === 3 && (
              <div className="space-y-5">
                <div className="bg-[#FF5722]/5 border border-[#FF5722]/20 rounded-xl p-4 mb-2">
                  <h4 className="text-[#FF5722] font-medium text-sm mb-2 flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Riepilogo
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <span>Cliente: <strong className="text-slate-900">{form.clientType}</strong></span>
                    <span>Edificio: <strong className="text-slate-900">{form.buildingType}</strong></span>
                    <span>Ruolo: <strong className="text-slate-900">{form.ownerType}</strong></span>
                    <span>Copertura: <strong className="text-slate-900">{form.structureType}</strong></span>
                    <span>Superficie: <strong className="text-slate-900">{form.surface}</strong></span>
                    <span>Provincia: <strong className="text-slate-900">{form.province}</strong></span>
                  </div>
                </div>

                <input type="text" name="website" value={form.website} onChange={(e) => update('website', e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="calc-name"
                    type="text"
                    placeholder="Il tuo nome *"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    required
                    className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                  />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="calc-phone"
                    type="tel"
                    placeholder="Numero di telefono *"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    required
                    className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                  />
                </div>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="calc-email"
                    type="email"
                    placeholder="La tua email *"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    required
                    className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    Riceverai il preventivo personalizzato entro 24 ore
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    100% gratuito e senza impegno
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex-1 py-3.5 rounded-xl text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
                  >
                    Indietro
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    data-testid="calc-submit"
                    className="flex-1 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-[#FF5722]/20"
                  >
                    {isSubmitting ? 'Invio in corso...' : 'Ricevi Preventivo Gratuito'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default CalcolatoreRisparmio;
